type PhoneOwnerInfo = {
    tabId: string;
    claimedAt: number;
    heartbeatAt: number;
};

type PhoneTabState = {
    tabId: string;
    isOwner: boolean;
    ownerTabId?: string;
};

type PhoneTabListener = (state: PhoneTabState) => void;

type PhoneTabMessage =
    | { type: "heartbeat"; tabId: string }
    | { type: "claim"; tabId: string; force?: boolean }
    | { type: "release"; tabId: string }
    | { type: "focus-owner"; tabId: string };

export class PhoneTabCoordinator {
    private static readonly OWNER_KEY = "rinvox-phone-owner";
    private static readonly CHANNEL_NAME = "rinvox-phone-channel";
    private static readonly HEARTBEAT_MS = 2000;
    private static readonly STALE_MS = 6000;

    private readonly tabId: string;
    private readonly channel: BroadcastChannel;
    private readonly listeners = new Set<PhoneTabListener>();

    private heartbeatTimer: number | null = null;
    private monitorTimer: number | null = null;

    public static instance: PhoneTabCoordinator = new PhoneTabCoordinator();

    constructor() {
        this.tabId = this.createTabId();
        this.channel = new BroadcastChannel(PhoneTabCoordinator.CHANNEL_NAME);

        this.channel.onmessage = (event: MessageEvent<PhoneTabMessage>) => {
            this.handleMessage(event.data);
        };

        window.addEventListener("storage", this.handleStorage);
        window.addEventListener("beforeunload", this.handleBeforeUnload);
    }

    public start(): void {
        this.tryBecomeOwnerIfNeeded();
        this.startHeartbeat();
        this.startMonitor();
        this.emit();
    }

    public destroy(): void {
        this.stopHeartbeat();
        this.stopMonitor();

        window.removeEventListener("storage", this.handleStorage);
        window.removeEventListener("beforeunload", this.handleBeforeUnload);

        this.channel.close();

        if (this.isOwner()) {
            this.releaseOwnership();
        }
    }

    public subscribe(listener: PhoneTabListener): () => void {
        this.listeners.add(listener);
        listener(this.getState());

        return () => {
            this.listeners.delete(listener);
        };
    }

    public getState(): PhoneTabState {
        const owner = this.getOwner();
        return {
            tabId: this.tabId,
            isOwner: owner?.tabId === this.tabId,
            ownerTabId: owner?.tabId
        };
    }

    public isOwner(): boolean {
        return this.getOwner()?.tabId === this.tabId;
    }

    public claimOwnership(force = false): void {
        const owner = this.getOwner();

        if (!force && owner && owner.tabId !== this.tabId && !this.isStale(owner)) {
            this.emit();
            return;
        }

        const nextOwner: PhoneOwnerInfo = {
            tabId: this.tabId,
            claimedAt: Date.now(),
            heartbeatAt: Date.now()
        };

        this.setOwner(nextOwner);
        try {
            this.channel.postMessage({ type: "claim", tabId: this.tabId, force });
        }
        catch (e) {
            console.warn("BroadcastChannel is already closed.", e);
        }
        this.emit();
    }

    public releaseOwnership(): void {
        const owner = this.getOwner();
        if (!owner || owner.tabId !== this.tabId) {
            return;
        }

        localStorage.removeItem(PhoneTabCoordinator.OWNER_KEY);
        try {
            this.channel.postMessage({ type: "release", tabId: this.tabId });
        }
        catch (e) {
            console.warn("BroadcastChannel is already closed.", e);
        }
        this.emit();
    }

    public focusOwnerTab(): void {
        const owner = this.getOwner();
        if (!owner) return;

        try {
            this.channel.postMessage({ type: "focus-owner", tabId: owner.tabId });
        }
        catch (e) {
            console.warn("BroadcastChannel is already closed.", e);
        }
    }

    private tryBecomeOwnerIfNeeded(): void {
        const owner = this.getOwner();

        if (!owner || this.isStale(owner)) {
            this.claimOwnership(true);
        }
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();

        this.heartbeatTimer = window.setInterval(() => {
            if (!this.isOwner()) return;

            const owner = this.getOwner();
            if (!owner || owner.tabId !== this.tabId) return;

            this.setOwner({
                ...owner,
                heartbeatAt: Date.now()
            });

            try {
                this.channel.postMessage({ type: "heartbeat", tabId: this.tabId });
            }
            catch (e) {

            }
            this.emit();
        }, PhoneTabCoordinator.HEARTBEAT_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer !== null) {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private startMonitor(): void {
        this.stopMonitor();

        this.monitorTimer = window.setInterval(() => {
            const owner = this.getOwner();

            if (!owner || this.isStale(owner)) {
                this.claimOwnership(true);
            } else {
                this.emit();
            }
        }, 1500);
    }

    private stopMonitor(): void {
        if (this.monitorTimer !== null) {
            window.clearInterval(this.monitorTimer);
            this.monitorTimer = null;
        }
    }

    private handleMessage(message: PhoneTabMessage): void {
        if (!message) return;

        if (message.type === "claim") {
            this.emit();
            return;
        }

        if (message.type === "release") {
            this.tryBecomeOwnerIfNeeded();
            this.emit();
            return;
        }

        if (message.type === "heartbeat") {
            this.emit();
            return;
        }

        if (message.type === "focus-owner") {
            if (message.tabId === this.tabId) {
                window.focus();
                closeBrowserPhoneIfExists();
            }
        }
    }

    private handleStorage = (event: StorageEvent) => {
        if (event.key !== PhoneTabCoordinator.OWNER_KEY) return;
        this.emit();
    };

    private handleBeforeUnload = () => {
        if (this.isOwner()) {
            this.releaseOwnership();
        }
    };

    private getOwner(): PhoneOwnerInfo | null {
        const raw = localStorage.getItem(PhoneTabCoordinator.OWNER_KEY);
        if (!raw) return null;

        try {
            return JSON.parse(raw) as PhoneOwnerInfo;
        } catch {
            return null;
        }
    }

    private setOwner(owner: PhoneOwnerInfo): void {
        localStorage.setItem(PhoneTabCoordinator.OWNER_KEY, JSON.stringify(owner));
    }

    private isStale(owner: PhoneOwnerInfo): boolean {
        return Date.now() - owner.heartbeatAt > PhoneTabCoordinator.STALE_MS;
    }

    private emit(): void {
        const state = this.getState();
        this.listeners.forEach(listener => listener(state));
    }

    private createTabId(): string {
        return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
}

function closeBrowserPhoneIfExists() {
    const event = new CustomEvent("rinvox-phone-owner-focus");
    window.dispatchEvent(event);
}